/**
 * AssignTaskDialog — WORKFLOW-OPS-001-PHASE-2A (P0-B)
 * ====================================================
 *
 * Operational assignment dialog used by the workflow page. Replaces the
 * Phase-1 `window.prompt`-based flow.
 *
 *   - Loads canonical clinic agents via GET /api/clinic/agents.
 *   - Lets the coordinator pick a role and/or a specific user.
 *   - Submits via the existing /assign or /transfer endpoint via the
 *     hook handler the parent passes in.
 *   - No backend changes. No new authority. Reuses assignment_log SSOT.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { api } from '../utils/api';

const PROVIDER_ROLES = [
  { value: '', label: '— role unchanged —' },
  { value: 'provider_admin', label: 'provider_admin' },
  { value: 'provider_coordinator', label: 'provider_coordinator' },
  { value: 'provider_doctor', label: 'provider_doctor' },
  { value: 'provider_sales_rep', label: 'provider_sales_rep' },
  { value: 'provider_after_sales_rep', label: 'provider_after_sales_rep' },
  { value: 'provider_accountant', label: 'provider_accountant' },
];

export default function AssignTaskDialog({
  open,
  onOpenChange,
  mode, // 'assign' | 'transfer'
  task,
  onSubmit,
}) {
  const [role, setRole] = useState('');
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form whenever the dialog (re)opens for a different task.
  useEffect(() => {
    if (!open) return;
    setRole('');
    setUserId(task?.assigned_to_user_id || '');
    setReason(mode === 'transfer' ? 'transfer' : 'assign');
    setError(null);
  }, [open, mode, task]);

  // Load clinic agents (best-effort; falls open on 403).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAgentsLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/clinic/agents');
        if (!cancelled) setAgents(Array.isArray(data?.agents) ? data.agents : []);
      } catch {
        if (!cancelled) setAgents([]);
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredAgents = useMemo(() => {
    if (!role) return agents;
    return agents.filter((a) => Array.isArray(a.roles) && a.roles.includes(role));
  }, [agents, role]);

  const submit = async () => {
    if (!role && !userId) {
      setError('Pick a role, a user, or both.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Parent provides the actual hook call (assignTask or transferTask).
      // Body shape matches the backend's Assign/Transfer request models.
      const body = mode === 'transfer'
        ? {
            new_assigned_role: role || null,
            new_assigned_to_user_id: userId || null,
            reason: reason || 'transfer',
          }
        : {
            assigned_role: role || null,
            assigned_to_user_id: userId || null,
            reason: reason || 'assign',
          };
      await onSubmit(body);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message;
      setError(typeof detail === 'string' ? detail : (detail?.error || 'Operation failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="assign-task-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'transfer' ? 'Reassign task' : 'Assign task'}
          </DialogTitle>
          <DialogDescription className="break-words">
            <span className="font-medium">{task.title || '(untitled task)'}</span>
            <span className="block mt-1 text-xs font-mono text-gray-500">
              {task.milestone_task_id || task.id}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="assign-role">Role</Label>
            <select
              id="assign-role"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setUserId('');
              }}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              data-testid="assign-task-dialog-role"
            >
              {PROVIDER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="assign-user">Specific user (optional)</Label>
            <select
              id="assign-user"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              disabled={agentsLoading}
              data-testid="assign-task-dialog-user"
            >
              <option value="">— no specific user —</option>
              {filteredAgents.map((a) => (
                <option key={a.user_id || a.agent_id} value={a.user_id || a.agent_id}>
                  {(a.display_name || a.name || a.user_id || a.agent_id)}
                  {Array.isArray(a.roles) && a.roles.length ? ` · ${a.roles.join(', ')}` : ''}
                </option>
              ))}
            </select>
            {agentsLoading && (
              <p className="text-[11px] text-gray-400">Loading clinic users…</p>
            )}
            {!agentsLoading && agents.length === 0 && (
              <p className="text-[11px] text-amber-600">
                No clinic agents available. You can still assign by role or by raw user id below.
              </p>
            )}
            <Input
              type="text"
              placeholder="…or paste a user id manually"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="assign-task-dialog-user-id-input"
              className="mt-1"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="assign-reason">Reason / note</Label>
            <Input
              id="assign-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="assign-task-dialog-reason"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600" data-testid="assign-task-dialog-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="assign-task-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="assign-task-dialog-submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? 'Saving…' : (mode === 'transfer' ? 'Reassign' : 'Assign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
